import { useState, FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { supabase } from '../../utils/supabase'
import { apiFetch } from '../../api/client'
import { termsAcceptanceSignupMetadata } from './termsAcceptanceMetadata'
import { createDebug } from '../../utils/debugLog'
import {
  MIN_PASSWORD_LENGTH,
  PASSWORD_RULES,
  describePasswordError,
  getPasswordRuleTranslationKey,
  validatePassword,
} from '../../utils/passwordPolicy'
import TermsAcknowledgment from './TermsAcknowledgment'
import { AuthErrorCtx } from './humanizeAuthError'
import { mapAuthError } from './mapAuthError'
import { normalizeLocale } from '../../i18n/supportedLocales'
import {
  trackSigninFailed,
  trackSignupClicked,
  trackSignupSubmitted,
  type SigninFailureReason,
} from '../../analytics/events'
import { trackInteraction } from '../../analytics/interactionAnalytics'

const log = createDebug('dw:auth-password-policy:signup')

/** The provider-error shape we read (GoTrue AuthError matches structurally). */
interface SigninErrorLike {
  code?: string
  status?: number
  message?: string
}

/**
 * Classify a sign-in failure into a provider-safe class for analytics (RFC-0974
 * §7.3) — NEVER account-existence (anti-enumeration). Returns null when the
 * failure can't be safely classified (then no event is emitted).
 */
function classifySigninFailure(err: SigninErrorLike | null): SigninFailureReason | null {
  const code = (err?.code || '').toLowerCase()
  const msg = (err?.message || '').toLowerCase()
  if (code === 'email_not_confirmed' || msg.includes('not confirmed')) return 'email_not_confirmed'
  if (err?.status === 429 || code.includes('rate') || msg.includes('rate limit')) {
    return 'rate_limited'
  }
  if (code === 'invalid_credentials' || err?.status === 400 || msg.includes('invalid login')) {
    return 'invalid_credentials'
  }
  return null
}

/** Emit `auth_signin_failed` for a classifiable failure (best-effort, no-op else). */
function emitSigninFailed(err: SigninErrorLike | null): void {
  const reason = classifySigninFailure(err)
  if (reason) trackSigninFailed({ reason })
}

function isWellFormedEmail(email: string): boolean {
  const trimmed = email.trim().toLowerCase()
  const parts = trimmed.split('@')
  return parts.length === 2 && parts[0].length > 0 && parts[1].length > 0
}

export default function EmailLoginForm() {
  const { t, i18n } = useTranslation('login')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isSignUp, setIsSignUp] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<'signup' | 'reset' | null>(null)
  // Explicit ToS consent. Unticked by default; gates Create Account. Sign-in is
  // not account creation, so it is only consulted while `isSignUp`.
  const [termsAccepted, setTermsAccepted] = useState(false)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)

    const trimmedEmail = email.trim().toLowerCase()

    if (!isWellFormedEmail(trimmedEmail)) {
      setError(t('enterValidEmail'))
      return
    }

    // Email-domain allowlisting is enforced server-side (apps/api deps.py +
    // the auth.users DB trigger). The client no longer hardcodes a domain so
    // any approved org/vendor domain can sign up; the server rejects the rest.

    // Strict policy applies to NEW passwords only (signup). Sign-in must stay
    // lenient: existing users may have shorter legacy passwords and the server
    // never enforces complexity at login — gating here would lock them out.
    if (isSignUp) {
      // Consent gate. The submit button is already disabled while unticked, so
      // this only fires if that gate is ever bypassed — fail loudly rather than
      // create an account without a recorded acceptance.
      if (!termsAccepted) {
        setError(t('auth:termsRequired'))
        return
      }

      const result = validatePassword(password)
      if (!result.ok) {
        log('reject failedRules=%o len=%d', result.failedRuleIds, password.length)
        setError(describePasswordError(result))
        return
      }
    } else if (password.length === 0) {
      setError(t('enterPassword'))
      return
    }

    setLoading(true)

    // The shared catch below covers both signup and sign-in awaits; label a
    // thrown (network/unexpected) error with the flow the user was actually in.
    const flowCtx: AuthErrorCtx = isSignUp
      ? import.meta.env.VITE_SKIP_EMAIL_CONFIRMATION === 'true'
        ? 'signup-admin'
        : 'signup-standard'
      : 'signin'

    try {
      if (isSignUp) {
        // RFC-0974: the registration form was submitted (pre-auth funnel).
        trackSignupSubmitted({ method: 'email' })
        // Extract display name from email (part before @)
        const displayName = trimmedEmail.split('@')[0]

        if (import.meta.env.VITE_SKIP_EMAIL_CONFIRMATION === 'true') {
          // Bypass email confirmation: create user via backend admin API
          const res = await apiFetch('/auth/signup', {
            method: 'POST',
            skipAuth: true,
            // Acceptance rides the signup request so the migration-106 trigger
            // writes the audit row inside the same INSERT (RFC-1457).
            body: JSON.stringify({
              email: trimmedEmail,
              password,
              ...termsAcceptanceSignupMetadata(),
            }),
          })

          if (!res.ok) {
            const data = await res.json().catch(() => ({}))
            const detail = typeof data.detail === 'string' ? data.detail : ''
            setError(mapAuthError(t, 'signup-admin', { status: res.status, detail }))
            return
          }

          // User created with email pre-confirmed — sign in immediately
          const { error: signInError } = await supabase.auth.signInWithPassword({
            email: trimmedEmail,
            password,
          })

          if (signInError) {
            emitSigninFailed(signInError)
            setError(mapAuthError(t, 'signin', signInError))
            return
          }

          // Success - AuthContext will handle the session update. The
          // acceptance row was already written by the trigger, inside the
          // INSERT that created the user.
        } else {
          // Standard flow: Supabase sends a confirmation email
          const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
            email: trimmedEmail,
            password,
            options: {
              emailRedirectTo: window.location.origin,
              data: {
                display_name: displayName,
                full_name: displayName,
                // Display-only email-render locale so the very first auth email
                // (signup confirmation) is localized before any preferred-locale
                // PUT lands. Mirrors the backend `email_locale` metadata key; the
                // source of truth stays public.user_preferred_locales. Never used
                // for authz (S1-1). Not the retired `preferred_locale` store.
                email_locale: normalizeLocale(i18n.language) ?? 'en',
                // Acceptance keys read by the `record_terms_acceptance`
                // trigger (migration 106), which writes the audit row inside
                // this same user INSERT (RFC-1457).
                ...termsAcceptanceSignupMetadata(),
              },
            },
          })

          if (signUpError) {
            setError(mapAuthError(t, 'signup-standard', signUpError))
            return
          }

          // With email confirmations enabled, GoTrue represents a repeated signup
          // as a successful response whose obfuscated user has no identities. The
          // product deliberately surfaces that state so returning users do not wait
          // for a confirmation email that GoTrue never sends.
          const identities = signUpData?.user?.identities
          if (Array.isArray(identities) && identities.length === 0) {
            setError(t('accountExists'))
            return
          }

          setSuccess('signup')
        }
      } else {
        // Sign in with email + password
        trackInteraction('auth_signin_submitted', { method: 'email' })
        const { error: signInError } = await supabase.auth.signInWithPassword({
          email: trimmedEmail,
          password,
        })

        if (signInError) {
          // invalid_credentials / email_not_confirmed / rate-limit stay specific;
          // anything unexpected (5xx, network, unknown code) → contact-team.
          emitSigninFailed(signInError)
          setError(mapAuthError(t, 'signin', signInError))
          return
        }

        // Success - AuthContext will handle the session update
      }
    } catch (err) {
      setError(mapAuthError(t, flowCtx, err instanceof Object ? err : {}))
    } finally {
      setLoading(false)
    }
  }

  const handleForgotPassword = async () => {
    setError(null)
    const trimmedEmail = email.trim().toLowerCase()
    if (!isWellFormedEmail(trimmedEmail)) {
      setError(t('enterEmailFirst'))
      return
    }
    setLoading(true)
    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(trimmedEmail, {
        redirectTo: `${window.location.origin}/reset-password`,
      })
      if (resetError) {
        setError(mapAuthError(t, 'reset-request', resetError))
        return
      }
      setSuccess('reset')
    } catch (err) {
      setError(mapAuthError(t, 'reset-request', err instanceof Object ? err : {}))
    } finally {
      setLoading(false)
    }
  }

  if (success) {
    const isReset = success === 'reset'
    return (
      <div className="text-center p-4">
        <div className="text-[#8dd5d8] mb-2">
          <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>
        <h3 className="font-[Fustat] text-lg font-medium text-white mb-2">{t('checkYourEmail')}</h3>
        <p className="font-[Fustat] text-white/60 text-sm">
          {isReset ? t('resetSent') : t('confirmationSent')}{' '}
          <span className="text-white">{email}</span>
        </p>
        <p className="font-[Fustat] text-white/40 text-xs font-medium mt-2">
          {isReset ? t('clickToReset') : t('clickToActivate')}
        </p>
        <button
          onClick={() => {
            setSuccess(null)
            setIsSignUp(false)
            setPassword('')
          }}
          className="mt-4 font-[Fustat] text-base text-[#8dd5d8] hover:text-[#8dd5d8]/80 transition-colors"
        >
          {t('backToSignIn')}
        </button>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="email" className="block font-[Fustat] text-lg font-medium text-white/70 mb-1.5">
          {t('workEmail')}
        </label>
        <input
          id="email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t('emailPlaceholder')}
          className="w-full px-3.5 py-2.5 bg-white/[0.04] rounded-xl border border-white/10 text-lg font-medium text-white font-[Fustat] placeholder:text-white/30 focus:outline-none focus:border-[#8dd5d8]/60 transition-colors"
          required
          disabled={loading}
        />
        <p className="font-[Fustat] text-base text-white/40 mt-1.5">{t('approvedDomainsOnly')}</p>
      </div>

      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label htmlFor="password" className="block font-[Fustat] text-lg font-medium text-white/70">
            {t('password')}
          </label>
          {!isSignUp && (
            <button
              type="button"
              onClick={handleForgotPassword}
              disabled={loading}
              className="font-[Fustat] text-base text-[#8dd5d8] hover:text-[#8dd5d8]/80 disabled:opacity-50 transition-colors"
            >
              {t('forgotPassword')}
            </button>
          )}
        </div>
        <input
          id="password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder={
            isSignUp ? t('createPasswordPlaceholder', { minLength: MIN_PASSWORD_LENGTH }) : t('enterPasswordPlaceholder')
          }
          className="w-full px-3.5 py-2.5 bg-white/[0.04] rounded-xl border border-white/10 text-lg font-medium text-white font-[Fustat] placeholder:text-white/30 focus:outline-none focus:border-[#8dd5d8]/60 transition-colors"
          required
          disabled={loading}
          // Only constrain length client-side on signup. Sign-in stays lenient so
          // existing users with shorter legacy passwords are not blocked.
          minLength={isSignUp ? MIN_PASSWORD_LENGTH : undefined}
        />
        {isSignUp && (
          <ul className="mt-2 space-y-0.5" data-testid="password-requirements">
            {PASSWORD_RULES.map((rule) => {
              const met = rule.test(password)
              return (
                <li
                  key={rule.id}
                  data-testid={`password-rule-${rule.id}`}
                  data-met={met}
                  className={`font-[Fustat] text-base flex items-center gap-1.5 ${met ? 'text-green-400' : 'text-white/40'}`}
                >
                  <span aria-hidden>{met ? '✓' : '○'}</span>
                  {/* Rule copy lives in the `auth` namespace (shared with
                      ChangePasswordModal / ResetPasswordPage); this form's
                      default ns is `login`, so reference it cross-ns. */}
                  {rule.id === 'length'
                    ? t(`auth:${getPasswordRuleTranslationKey(rule.id)}`, { count: MIN_PASSWORD_LENGTH })
                    : t(`auth:${getPasswordRuleTranslationKey(rule.id)}`)}
                </li>
              )
            })}
          </ul>
        )}
      </div>

      {error && (
        <div
          role="alert"
          data-testid="auth-error"
          className="p-3 bg-destructive/15 border border-destructive/40 rounded-xl text-destructive text-base font-[Fustat]"
        >
          {error}
        </div>
      )}

      {/* RQ16043652: acknowledgment shown immediately above the account-creation
          action (signup only — plain Sign In is not account creation). */}
      {isSignUp && (
        <TermsAcknowledgment
          context="email-signup"
          accepted={termsAccepted}
          onAcceptedChange={setTermsAccepted}
        />
      )}

      <button
        type="submit"
        disabled={
          loading ||
          !email.trim() ||
          (isSignUp ? !validatePassword(password).ok || !termsAccepted : password.length === 0)
        }
        className="w-full h-12 rounded-[40px] bg-white text-black font-[Fustat] font-semibold text-lg hover:bg-white/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {loading ? (isSignUp ? t('creatingAccount') : t('signingIn')) : (isSignUp ? t('createAccount') : t('signIn'))}
      </button>

      <div className="text-center">
        <button
          type="button"
          onClick={() => {
            // RFC-0974: entering signup mode = the signup form opened.
            if (!isSignUp) trackSignupClicked({ entry_point: 'login_form_toggle' })
            setIsSignUp(!isSignUp)
            setError(null)
            // Leaving sign-up discards the tick, so coming back requires a
            // fresh, deliberate acceptance rather than a stale one.
            setTermsAccepted(false)
          }}
          className="font-[Fustat] text-base text-white/50 hover:text-white transition-colors"
        >
          {isSignUp ? t('alreadyHaveAccount') : t('noAccount')}
        </button>
      </div>
    </form>
  )
}
